import { AlertCircle, RefreshCw } from "lucide-react";
import PropTypes from "prop-types";
import React from "react";

/**
 * Error boundary component for individual message rendering
 * Catches and displays errors that occur during message component rendering
 * Prevents entire chat from crashing when a single message fails
 */
class MessageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const { messageId, onError } = this.props;

    // Log error details
    console.error("[MessageErrorBoundary] Error rendering message:", {
      messageId,
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });

    // Update state with error details
    this.setState((prevState) => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Call optional error callback
    if (onError) {
      onError(error, errorInfo, messageId);
    }
  }

  handleReset = () => {
    const { onReset, messageId } = this.props;

    console.log(
      "[MessageErrorBoundary] Resetting error state for message:",
      messageId
    );

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call optional reset callback
    if (onReset) {
      onReset(messageId);
    }
  };

  render() {
    const { hasError, error, errorCount } = this.state;
    const { children, messageId, fallback, showDetails } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return typeof fallback === "function"
          ? fallback(error, this.handleReset, messageId)
          : fallback;
      }

      // Default error UI
      return (
        <div
          className="my-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4"
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Error displaying message
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                This message couldn&apos;t be displayed due to a rendering
                error.
                {errorCount > 1 && ` (Failed ${errorCount} times)`}
              </p>

              {showDetails && error && (
                <details className="mt-2">
                  <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer hover:underline">
                    Show error details
                  </summary>
                  <pre className="mt-2 text-xs text-red-700 dark:text-red-300 overflow-x-auto bg-red-100 dark:bg-red-900/30 p-2 rounded">
                    {error.toString()}
                  </pre>
                </details>
              )}

              <button
                onClick={this.handleReset}
                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60 rounded-md transition-colors duration-200"
                aria-label="Try displaying this message again"
              >
                <RefreshCw className="h-4 w-4" />
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

MessageErrorBoundary.propTypes = {
  /** Child components to render (the message) */
  children: PropTypes.node.isRequired,

  /** Unique identifier for the message being rendered */
  messageId: PropTypes.string,

  /** Custom fallback UI (component or render function) */
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /** Whether to show detailed error information (for development) */
  showDetails: PropTypes.bool,

  /** Callback when an error is caught */
  onError: PropTypes.func,

  /** Callback when the error boundary is reset */
  onReset: PropTypes.func,
};

MessageErrorBoundary.defaultProps = {
  messageId: "unknown",
  fallback: null,
  showDetails: process.env.NODE_ENV === "development",
  onError: null,
  onReset: null,
};

export default MessageErrorBoundary;
